import { z } from 'zod'

/**
 * Time categories classify the kind of work behind an entry (client work,
 * admin, training, sales…). A category's `billableDefault` sets the entry's
 * billable flag when the user hasn't chosen one, ahead of the project, client
 * and org defaults. Non-billable categories are how internal cost is grouped.
 */
export const TimeCategorySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  key: z.string().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  order: z.number().default(0),
  isActive: z.boolean().default(true),
  billableDefault: z.boolean().default(false),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type TimeCategory = z.infer<typeof TimeCategorySchema>

export const CreateTimeCategorySchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().max(500).nullable().optional(),
  color: z.string().max(20).nullable().optional(),
  billableDefault: z.boolean().optional(),
})

export type CreateTimeCategory = z.infer<typeof CreateTimeCategorySchema>

export const UpdateTimeCategorySchema =
  CreateTimeCategorySchema.partial().extend({
    isActive: z.boolean().optional(),
    order: z.number().int().min(0).optional(),
  })

export type UpdateTimeCategory = z.infer<typeof UpdateTimeCategorySchema>

export const ReorderTimeCategoriesSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(200),
})

/** The category summary carried on a time entry. */
export const TimeEntryCategorySummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string().nullable().optional(),
  billableDefault: z.boolean().optional(),
})

export type TimeEntryCategorySummary = z.infer<
  typeof TimeEntryCategorySummarySchema
>

/** Generic starting catalog, seeded for every organization. */
export const DEFAULT_TIME_CATEGORIES = [
  {
    key: 'client_work',
    name: 'Client work',
    description: 'Delivery work billed to a client.',
    color: '#10B981',
    billableDefault: true,
  },
  {
    key: 'admin',
    name: 'Admin',
    description: 'Email, planning, timesheets, and general admin.',
    color: '#64748B',
    billableDefault: false,
  },
  {
    key: 'internal_meeting',
    name: 'Internal meeting',
    description: 'Team syncs, one-on-ones, and company meetings.',
    color: '#6366F1',
    billableDefault: false,
  },
  {
    key: 'training',
    name: 'Training',
    description: 'Courses, learning, onboarding, and certifications.',
    color: '#F59E0B',
    billableDefault: false,
  },
  {
    key: 'sales',
    name: 'Sales & business development',
    description: 'Proposals, pitches, and prospecting.',
    color: '#EC4899',
    billableDefault: false,
  },
  {
    key: 'marketing',
    name: 'Marketing',
    description: 'Content, campaigns, and brand work.',
    color: '#8B5CF6',
    billableDefault: false,
  },
  {
    key: 'process_improvement',
    name: 'Process improvement',
    description: 'Tooling, templates, and improving how the team works.',
    color: '#0EA5E9',
    billableDefault: false,
  },
] as const satisfies ReadonlyArray<{
  key: string
  name: string
  description: string
  color: string
  billableDefault: boolean
}>
